import { withBase } from '@hive/api';
import { useEffect, useState } from 'react';

/**
 * PWA (HU-013, RF-PWA-01): registra o service worker no build de produção, avisa quando há
 * versão nova (aplicada ao recarregar, sem perder o que a pessoa preenchia) e guarda o
 * pedido de instalação do navegador para o cartão "Instale o Hive como app".
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Listener = () => void;
let installEvent: BeforeInstallPromptEvent | null = null;
let waiting: ServiceWorker | null = null;
let updating = false;
const installListeners = new Set<Listener>();
const updateListeners = new Set<Listener>();
const UPDATED_FLAG = 'hive.pwa.updated';

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    installEvent = e as BeforeInstallPromptEvent;
    installListeners.forEach((l) => l());
  });
  window.addEventListener('appinstalled', () => {
    installEvent = null;
    installListeners.forEach((l) => l());
  });
}

export function registerServiceWorker() {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(withBase('/sw.js'), { scope: withBase('/') })
      .then((reg) => {
        const track = (sw: ServiceWorker | null) => {
          if (!sw) return;
          sw.addEventListener('statechange', () => {
            if (sw.state === 'installed' && navigator.serviceWorker.controller) {
              waiting = sw;
              updateListeners.forEach((l) => l());
            }
          });
        };
        if (reg.waiting && navigator.serviceWorker.controller) {
          waiting = reg.waiting;
          updateListeners.forEach((l) => l());
        }
        reg.addEventListener('updatefound', () => track(reg.installing));
        // Procura versão nova de hora em hora enquanto a console fica aberta.
        window.setInterval(() => void reg.update().catch(() => undefined), 60 * 60 * 1000);
      })
      .catch(() => undefined);
    // recarrega só quando a pessoa pediu a versão nova (applyUpdate). Na primeira visita o service worker assume a
    // página sozinho (clients.claim) e também dispara controllerchange: recarregar ali apagava o que ela digitava
    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading || !updating) return;
      reloading = true;
      window.location.reload();
    });
  });
}

/** Aplica a versão nova: o service worker que espera assume e a página recarrega. */
export function applyUpdate() {
  try {
    window.sessionStorage.setItem(UPDATED_FLAG, '1');
  } catch {
    /* sem armazenamento */
  }
  updating = true;
  if (waiting) waiting.postMessage('SKIP_WAITING');
  else window.location.reload();
}

/** A página acabou de recarregar por causa de uma atualização? (mostra o aviso "Atualizado"). */
export function consumeUpdatedFlag(): boolean {
  try {
    const v = window.sessionStorage.getItem(UPDATED_FLAG) === '1';
    window.sessionStorage.removeItem(UPDATED_FLAG);
    return v;
  } catch {
    return false;
  }
}

export function useUpdateReady(): boolean {
  const [ready, setReady] = useState(waiting != null);
  useEffect(() => {
    const l = () => setReady(waiting != null);
    updateListeners.add(l);
    return () => void updateListeners.delete(l);
  }, []);
  return ready;
}

export function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
}

export function useInstallPrompt(): { available: boolean; install: () => Promise<boolean> } {
  const [available, setAvailable] = useState(installEvent != null);
  useEffect(() => {
    const l = () => setAvailable(installEvent != null);
    installListeners.add(l);
    return () => void installListeners.delete(l);
  }, []);
  return {
    available,
    async install() {
      if (!installEvent) return false;
      await installEvent.prompt();
      const choice = await installEvent.userChoice;
      installEvent = null;
      installListeners.forEach((l) => l());
      return choice.outcome === 'accepted';
    },
  };
}

const DISMISS_KEY = 'hive.pwa.dismissed';
export function installHintDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}
export function dismissInstallHint() {
  try {
    window.localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* sem armazenamento */
  }
}
